import { useState, useMemo, useCallback } from 'react';
import _ from 'lodash';
import { getQuestionnaireTemplate } from '../services/questionnaire';
import { RequestStatus } from '../../model';
import type { QuestionnaireTemplate, QuestionnaireQuestion } from '../types/questionnaire';
import { QUESTIONNAIRE_SECTION_COLORS } from '../tokens/colors';

interface FlatQuestion extends QuestionnaireQuestion {
  secIdx: number
  secTitle: string
  secColor: string
  secInstructions?: string
  localIdx: number
  localTotal: number
}

const useQuestionnaireTemplate = (templateId: string, templateVersion: number) => {
  const [template, setTemplate] = useState<QuestionnaireTemplate | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  const handleLoadTemplate = useCallback(async () => {
    setIsLoading(true);
    setError('');
    const result = await getQuestionnaireTemplate(templateId, templateVersion);
    if (result.status === RequestStatus.SUCCESS && result.data) {
      setTemplate(result.data);
    } else {
      setError(result.error || 'Failed to load questionnaire template');
    }
    setIsLoading(false);
  }, [templateId, templateVersion]);

  const flatQuestions: FlatQuestion[] = useMemo(() => {
    if (!template) return [];
    return _.flatMap(template.parts, (part, partIdx) => {
      const color = part.color || QUESTIONNAIRE_SECTION_COLORS[partIdx % QUESTIONNAIRE_SECTION_COLORS.length];
      return part.questions.map((question, qIdx) => ({
        ...question,
        secIdx: partIdx,
        secTitle: part.title,
        secColor: color,
        secInstructions: part.instructions,
        localIdx: qIdx,
        localTotal: part.questions.length,
      }));
    });
  }, [template]);

  return {
    template,
    isLoading,
    error,
    flatQuestions,
    handleLoadTemplate,
  };
};

export type { FlatQuestion };
export default useQuestionnaireTemplate;
